import { IConfigLoader } from '../../Contracts/Loaders/IConfigLoader'
import { ILoader } from '../../Contracts/Loaders/ILoader'
import { loggerManager } from '../../Managers/Logger/LoggerManager'

/**
 * Error categories for better error handling
 */
export enum LoaderErrorType {
    NETWORK = 'NETWORK',
    VALIDATION = 'VALIDATION',
    SYSTEM = 'SYSTEM',
    DEPENDENCY = 'DEPENDENCY',
    TIMEOUT = 'TIMEOUT',
    UNKNOWN = 'UNKNOWN',
}

/**
 * Enhanced error class for loaders
 */
export class LoaderError extends Error {
    constructor(
        message: string,
        public readonly type: LoaderErrorType,
        public readonly loaderName: string,
        public readonly originalError?: Error,
        public readonly context?: Record<string, any>
    ) {
        super(message)
        this.name = 'LoaderError'
        
        // Capture stack trace
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, LoaderError)
        }
    }

    /**
     * Get formatted error message with full context
     */
    getFullMessage(): string {
        let msg = `[${this.loaderName}] ${this.type}: ${this.message}`
        
        if (this.context) {
            msg += `\nContext: ${JSON.stringify(this.context, null, 2)}`
        }
        
        if (this.originalError) {
            msg += `\nOriginal error: ${this.originalError.message}`
            if (this.originalError.stack) {
                msg += `\nStack: ${this.originalError.stack}`
            }
        }
        
        return msg
    }

    /**
     * Check if error is retryable
     */
    isRetryable(): boolean {
        return this.type === LoaderErrorType.NETWORK || this.type === LoaderErrorType.TIMEOUT
    }
}

export abstract class BaseLoader implements ILoader {
    protected config: IConfigLoader
    private cache: Map<string, any> = new Map()
    private metrics: {
        loadTime: number
        cacheHits: number
        cacheMisses: number
        errors: number
        lastLoadTimestamp?: number
    } = {
        loadTime: 0,
        cacheHits: 0,
        cacheMisses: 0,
        errors: 0,
    }

    constructor(config: IConfigLoader) {
        this.config = config
    }

    abstract load(): Promise<any>

    getName(): string {
        return this.config.name
    }

    isClientSafe(): boolean {
        return this.config.clientSafe
    }

    getPriority(): number {
        return this.config.priority
    }

    getDependencies(): string[] {
        return this.config.dependencies || []
    }

    protected isServer(): boolean {
        return typeof window === 'undefined'
    }

    protected isClient(): boolean {
        return typeof window !== 'undefined'
    }

    /**
     * Get environment variable safely
     */
    protected getEnvVar(key: string, defaultValue?: string): string | undefined {
        if (typeof import.meta !== 'undefined' && import.meta.env) {
            return import.meta.env[key] || defaultValue
        }
        return defaultValue
    }

    /**
     * Logger with prefix for better debugging
     */
    protected log(message: string, level: 'info' | 'warn' | 'error' = 'info'): void {
        const loaderName = this.getName()
        
        switch (level) {
            case 'info':
                loggerManager.info(message, 'loader', loaderName)
                break
            case 'warn':
                loggerManager.warn(message, 'loader', loaderName)
                break
            case 'error':
                loggerManager.error(message, 'loader', loaderName)
                break
        }
    }

    /**
     * Create a categorized error
     */
    protected createError(
        message: string,
        type: LoaderErrorType,
        originalError?: Error,
        context?: Record<string, any>
    ): LoaderError {
        return new LoaderError(message, type, this.getName(), originalError, context)
    }

    /**
     * Categorize an unknown error
     */
    protected categorizeError(error: unknown): LoaderErrorType {
        if (error instanceof LoaderError) {
            return error.type
        }

        const errorMessage = error instanceof Error ? error.message.toLowerCase() : String(error).toLowerCase()

        if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
            return LoaderErrorType.NETWORK
        }
        if (errorMessage.includes('timeout')) {
            return LoaderErrorType.TIMEOUT
        }
        if (errorMessage.includes('validation') || errorMessage.includes('invalid')) {
            return LoaderErrorType.VALIDATION
        }
        if (errorMessage.includes('dependency') || errorMessage.includes('require')) {
            return LoaderErrorType.DEPENDENCY
        }

        return LoaderErrorType.SYSTEM
    }

    /**
     * Retry logic with exponential backoff and error categorization
     */
    protected async loadWithRetry<T>(fn: () => Promise<T>, retries = 3, context?: Record<string, any>): Promise<T> {
        for (let i = 0; i < retries; i++) {
            try {
                return await fn()
            } catch (error) {
                const errorType = this.categorizeError(error)
                const loaderError =
                    error instanceof LoaderError
                        ? error
                        : this.createError(
                              `Failed on attempt ${i + 1}/${retries}`,
                              errorType,
                              error instanceof Error ? error : new Error(String(error)),
                              context
                          )

                // Don't retry non-retryable errors
                if (!loaderError.isRetryable() || i === retries - 1) {
                    this.log(loaderError.getFullMessage(), 'error')
                    throw loaderError
                }

                this.log(`Retry attempt ${i + 1}/${retries}: ${loaderError.message}`, 'warn')
                await new Promise((resolve) => setTimeout(resolve, 100 * Math.pow(2, i)))
            }
        }
        throw this.createError('Max retries exceeded', LoaderErrorType.SYSTEM, undefined, context)
    }

    /**
     * Cache results to avoid redundant processing
     */
    protected async getCached<T>(key: string, factory: () => Promise<T>): Promise<T> {
        if (this.cache.has(key)) {
            this.metrics.cacheHits++
            this.log(`Cache hit for key: ${key}`, 'info')
            return this.cache.get(key)
        }
        this.metrics.cacheMisses++
        this.log(`Cache miss for key: ${key}`, 'info')
        return factory().then((result) => {
            this.cache.set(key, result)
            return result
        })
    }

    /**
     * Clear cache (useful for hot reload scenarios)
     */
    protected clearCache(): void {
        this.cache.clear()
        this.log('Cache cleared', 'info')
    }

    /**
     * Get cache statistics
     */
    protected getCacheStats(): { size: number; keys: string[]; hits: number; misses: number; hitRate: number } {
        const total = this.metrics.cacheHits + this.metrics.cacheMisses
        const hitRate = total > 0 ? this.metrics.cacheHits / total : 0

        return {
            size: this.cache.size,
            keys: Array.from(this.cache.keys()),
            hits: this.metrics.cacheHits,
            misses: this.metrics.cacheMisses,
            hitRate: Math.round(hitRate * 100) / 100,
        }
    }

    /**
     * Get performance metrics
     */
    public getMetrics() {
        const memoryUsage = this.estimateMemoryUsage()

        return {
            ...this.metrics,
            memoryUsageBytes: memoryUsage,
            memoryUsageMB: Math.round((memoryUsage / 1024 / 1024) * 100) / 100,
            cacheStats: this.getCacheStats(),
        }
    }

    /**
     * Estimate memory usage of cache
     */
    private estimateMemoryUsage(): number {
        let bytes = 0
        for (const value of this.cache.values()) {
            bytes += this.estimateValueSize(value)
        }
        return bytes
    }

    /**
     * Rough estimation of value size in bytes
     */
    private estimateValueSize(value: any): number {
        if (value === null || value === undefined) return 0
        if (typeof value === 'boolean') return 4
        if (typeof value === 'number') return 8
        if (typeof value === 'string') return value.length * 2
        if (Array.isArray(value)) {
            return value.reduce((acc: number, item) => acc + this.estimateValueSize(item), 0)
        }
        if (typeof value === 'object') {
            return Object.values(value).reduce((acc: number, val) => acc + this.estimateValueSize(val), 0)
        }
        return 0
    }

    /**
     * Measure execution time of an async function
     */
    protected async measureTime<T>(name: string, fn: () => Promise<T>): Promise<T> {
        const start = performance.now()
        try {
            const result = await fn()
            const duration = performance.now() - start
            this.metrics.loadTime = duration
            this.metrics.lastLoadTimestamp = Date.now()
            this.log(`${name} completed in ${Math.round(duration)}ms`, 'info')
            return result
        } catch (error) {
            this.metrics.errors++
            throw error
        }
    }

    /**
     * Health check - can be overridden by child loaders
     */
    public async healthCheck(): Promise<{
        healthy: boolean
        issues: string[]
        metrics: any
    }> {
        const issues: string[] = []

        // Check if loader has been initialized
        if (!this.metrics.lastLoadTimestamp) {
            issues.push('Loader has not been initialized')
        }

        // Check error rate
        if (this.metrics.errors > 10) {
            issues.push(`High error count: ${this.metrics.errors}`)
        }

        // Check cache size
        const cacheSize = this.cache.size
        if (cacheSize > 1000) {
            issues.push(`Cache size too large: ${cacheSize} items`)
        }

        // Check memory usage
        const memoryMB = this.getMetrics().memoryUsageMB
        if (memoryMB > 100) {
            issues.push(`High memory usage: ${memoryMB}MB`)
        }

        return {
            healthy: issues.length === 0,
            issues,
            metrics: this.getMetrics(),
        }
    }

    /**
     * Reset metrics
     */
    public resetMetrics(): void {
        this.metrics = {
            loadTime: 0,
            cacheHits: 0,
            cacheMisses: 0,
            errors: 0,
        }
        this.log('Metrics reset', 'info')
    }
}
